import type { DataSource } from '@unifyapps/defs/types/dataSource';
import { EventTypeEnum } from '@unifyapps/defs/types/event';
import useEventCallback from '@unifyapps/hooks/useEventCallback';
import type { JSONSchema7 } from 'json-schema';
import _values from 'lodash/values';
import { useEffect, useMemo } from 'react';
import type { QueryRequestResult } from '../../../slices/DataSourceControllerSlice/types';
import { useDSCBlockEvents } from '../context/selectors';
import type { QueryNode } from '../types';

//useQuery callbacks have been discontinued
//https://github.com/TanStack/query/discussions/5279
// so executing callback in useEffect

type UseExecuteDataSourceCallbacksParams = {
  callbacks: DataSource['callbacks'];
  queryResult: Pick<QueryRequestResult, 'error' | 'isLoading' | 'isFetching'>;
  onQueryCompletion: QueryNode['onQueryCompletion'];
  requestId: string;
  id: string;
  outputSchema?: JSONSchema7;
  disabled?: boolean;
};

export function useExecuteDataSourceCallbacks({
  callbacks,
  queryResult,
  onQueryCompletion,
  requestId,
  id,
  outputSchema,
  disabled,
}: UseExecuteDataSourceCallbacksParams) {
  const { onSuccessEvents, onFailureEvents } = useMemo(() => {
    return {
      onSuccessEvents: callbacks?.successEvents ?? _values(callbacks?.onSuccess),
      onFailureEvents: callbacks?.failureEvents ?? _values(callbacks?.onFailure),
    };
  }, [
    callbacks?.failureEvents,
    callbacks?.onFailure,
    callbacks?.onSuccess,
    callbacks?.successEvents,
  ]);

  const { emitEvent: emitSuccessEvent } = useDSCBlockEvents(onSuccessEvents);
  const { emitEvent: emitErrorEvent } = useDSCBlockEvents(onFailureEvents);

  const onSuccess = useEventCallback(() => {
    void emitSuccessEvent({ eventType: EventTypeEnum.OnSuccess });
  });

  const onError = useEventCallback(() => {
    void emitErrorEvent({ eventType: EventTypeEnum.OnFailure });
  });

  useEffect(() => {
    if (queryResult.isLoading || queryResult.isFetching || disabled) {
      return;
    }

    // not using requestAnimationFrame because calling
    // success callbacks is heavy operation
    setTimeout(() => {
      // handle error or success based on query result
      // and then call the onQueryCompletion callback
      if (queryResult.error) {
        onError();
      } else {
        onSuccess();
      }

      onQueryCompletion({ requestId, id, outputSchema });
    }, 0);
  }, [
    disabled,
    id,
    onError,
    onQueryCompletion,
    onSuccess,
    outputSchema,
    queryResult.error,
    queryResult.isFetching,
    queryResult.isLoading,
    requestId,
  ]);
}
